// Core
import React from 'react';

// Redux
import { connect } from 'react-redux';
import { updateFilters, goToRatings, goToTags } from '../../actions';

// Components
import { BaseStat } from '../BaseStat';
import {
  GeckoDropdown,
  GeckoIcon,
  GeckoLabel,
  GeckoModalService,
  GeckoRow,
} from 'ui';
import FilterBar from '../../components/FilterBar';

import NewConversations from './widgets/NewConversations';
import ConversationRatings from './widgets/ConversationRatings';
import ClosedConversations from './widgets/ClosedConversations';
import MedianFirstResponse from './widgets/MedianFirstResponse';
import ConversationsByChannel from './widgets/ConversationsByChannel';
import MedianConversationLength from './widgets/MedianConversationLength';
import ConversationsByChannelType from './widgets/ConversationsByChannelType';
import ConversationsByTag from './widgets/ConversationsByTag';
import GeckoFeatures from 'services/gecko-features';
import MessagesSent from './widgets/MessagesSent';
import MessagesSentAnalysis from './widgets/MessagesSentAnalysis';
import './index.scss';
import moment from 'moment';
import ConversationPerHour from './widgets/ConversationPerHour';
import WorkHoursModal from '../../components/WorkHoursModal/WorkHoursModal';
import ChatGPTConversations from './widgets/ChatGPTConversations';
import { TooltipIdStr, Tooltip, OverlayTrigger } from 'ui';
import GeckoStore from 'services/gecko-store';

export class Conversations extends BaseStat {
  constructor(props) {
    super(props);
    this.ratingsSelectHandler.bind(this);
    this.tagsSelectHandler.bind(this);
  }

  ratingsSelectHandler = (rating) => {
    goToRatings(rating);
  };

  tagsSelectHandler = () => {
    goToTags();
  };

  widgets = [
    {
      title: 'Tag Summary',
      description:
        'The frequency of tags used in conversations within the filtered period',
      render: () => (
        <ConversationsByTag
          id="ConversationsByTag"
          tagsSelectHandler={this.tagsSelectHandler}
        />
      ),
      headerButton: {
        name: 'More Details',
        type: 'default',
        size: 'sm',
        handler: () => this.tagsSelectHandler(),
      },
    },
    {
      title: 'New Conversations',
      description:
        'The number of new conversations started by an inbound message, calculated from the date the conversation was first started',
      render: () => (
        <NewConversations
          id="NewConversations"
          currentPeriod={this.props.currentPeriod}
          currentPeriodRanges={this.props.currentPeriodRanges}
          previousPeriod={this.props.previousPeriod}
        />
      ),
    },
    {
      title: (
        <>
          ChatGPT Conversation Rate <GeckoLabel type="primary" name="New" />
          <span className="ChatGPTConversations-info-icon">
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip id={TooltipIdStr('ChatGPTConversations')}>
                  Currently ChatGPT stats are only valid from 4th January 2024
                </Tooltip>
              }
            >
              <span id={TooltipIdStr('ChatGPTConversations')}>
                <GeckoIcon icon="fas fa-exclamation-circle widget-tooltip" />
              </span>
            </OverlayTrigger>
          </span>
        </>
      ),
      description:
        'The number of conversations ChatGPT was a part of in relation to the number of conversations in total',
      render: () => (
        <ChatGPTConversations
          id="ChatGPTConversations"
          currentPeriod={this.props.currentPeriod}
          currentPeriodRanges={this.props.currentPeriodRanges}
          previousPeriod={this.props.previousPeriod}
        />
      ),
      hideWhen: () => !GeckoFeatures.can('ai-engine'),
    },
    {
      title: (
        <>
          Messages Sent Analysis
        </>
      ),
      description:
        'Displays the split between messages sent by IBM Watson, ChatGPT and Agents. Valid from 4th Jan 2024.',
      render: () => (
        <MessagesSentAnalysis
          id="MessagesSentAnalysis"
          currentPeriod={this.props.currentPeriod}
          currentPeriodRanges={this.props.currentPeriodRanges}
          previousPeriod={this.props.previousPeriod}
        />
      ),
    },
    {
      title: 'Conversations Started Out of Hours',
      description:
        'Conversations started in or out of your configured "office hours"',
      render: () => (
        <ConversationPerHour
          id="ConversationPerHour"
          filters={{
            startDate: this.props.startDate,
            endDate: this.props.endDate,
            filteredAgents: this.props.filteredAgents,
            filteredTeams: this.props.filteredTeams,
            filteredBots: this.props.filteredBots,
            filteredTags: this.props.filteredTags,
            filteredChannels: this.props.filteredChannels,
          }}
        />
      ),
      customHeaderElement: (
        <GeckoDropdown
          align="right"
          icon="far fa-cog"
          type="default"
          size="sm"
          items={[
            {
              name: (
                <span>
                  Set Working Hours
                </span>
              ),
              handler: () =>
                new GeckoModalService().open(() => (
                  <WorkHoursModal
                    filters={{
                      startDate: this.props.startDate,
                      endDate: this.props.endDate,
                      filteredAgents: this.props.filteredAgents,
                      filteredTeams: this.props.filteredTeams,
                      filteredBots: this.props.filteredBots,
                      filteredTags: this.props.filteredTags,
                      filteredChannels: this.props.filteredChannels,
                    }}
                  />
                )),
            },
          ]}
        />
      ),
    },
    {
      title: 'Conversation Ratings',
      description:
        'The number of conversations that received each of the available ratings. Ratings are associated to the Bot, Team or Agent who was last assigned when the conversation was closed.',
      render: () => (
        <ConversationRatings
          id="ConversationRatings"
          ratingsSelectHandler={this.ratingsSelectHandler}
        />
      ),
      headerButton: {
        name: 'More Details',
        type: 'default',
        size: 'sm',
        handler: () => this.ratingsSelectHandler(),
      },
    },
    {
      title: 'Closed Conversations',
      description:
        'The number of conversations with status closed, calculated from the time the conversation was closed',
      render: () => (
        <ClosedConversations
          id="ClosedConversations"
          currentPeriod={this.props.currentPeriod}
          currentPeriodRanges={this.props.currentPeriodRanges}
          previousPeriod={this.props.previousPeriod}
        />
      ),
    },
    {
      title: 'Median First Response Time',
      description:
        'The median time of your first response in new conversations, from the first inbound message to the first agent response',
      render: () => (
        <MedianFirstResponse
          id="MedianFirstResponse"
          currentPeriod={this.props.currentPeriod}
          currentPeriodRanges={this.props.currentPeriodRanges}
        />
      ),
    },
    {
      title: 'Median Conversation Length',
      description:
        'The median duration of a conversation, calculated from the time of the first incoming message in a new conversation until the last message once closed',
      render: () => (
        <MedianConversationLength
          id="MedianConversationLength"
          currentPeriod={this.props.currentPeriod}
          currentPeriodRanges={this.props.currentPeriodRanges}
        />
      ),
    },
    {
      title: 'Messages Sent',
      description:
        'Total number of messages sent within the filtered period, calculated from the date the message was sent',
      render: () => (
        <MessagesSent
          id="MessagesSent"
          currentPeriod={this.props.currentPeriod}
          currentPeriodRanges={this.props.currentPeriodRanges}
        />
      ),
      hideWhen: () => !GeckoFeatures.can('message-stats'),
    },
    {
      title: 'Conversations By Channel',
      description:
        'Total new conversations started within the filtered period, broken down by the channel that the first message originated from',
      render: () => <ConversationsByChannel id="ConversationsByChannel" />,
    },
    {
      title: 'Conversations By Channel Type',
      description:
        'Total new conversations started within the filtered period, grouped by channel type',
      render: () => (
        <ConversationsByChannelType id="ConversationsByChannelType" />
      ),
    },
  ];

  componentDidMount() {
    updateFilters({
      startDate: moment().subtract(7, 'days'),
      endDate: moment(),
      filteredAgents: [],
      filteredTags: [],
    });
  }

  render() {
    return (
      <div className="Stats-Conversations">
        <GeckoRow>
          <div className="col">
            <FilterBar
              filterByDates
              filterByAgents
              filterByBots
              filterByTags
              filterByChannels
              startDate={this.props.startDate}
              endDate={this.props.endDate}
              agents={this.props.filteredAgents}
              tags={this.props.filteredTags}
              filter={updateFilters}
            />
          </div>
        </GeckoRow>
        <div className="row Stats-Conversations-cards">
          {this.renderCards()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    fetchingConversationStats: state.statsRoute.fetchingConversationStats,
    fetchingConversationsByChannelStats:
      state.statsRoute.fetchingConversationByChannelStats,
    currentPeriod: state.statsRoute.conversationCurrentPeriod,
    currentPeriodRanges: state.statsRoute.conversationsCurrentPeriodSplit,
    previousPeriod: state.statsRoute.conversationPreviousPeriod,
    startDate: state.statsRoute.conversationStartDateFilter,
    endDate: state.statsRoute.conversationEndDateFilter,
    filteredAgents: state.statsRoute.filteredAgents,
    filteredTags: state.statsRoute.filteredTags,
    filteredTeams: state.statsRoute.filteredTeams,
    filteredBots: state.statsRoute.filteredBots,
    filteredChannels: state.statsRoute.filteredChannels,
  };
};

export default connect(mapStateToProps)(Conversations);
